import { Dispatch, SetStateAction, SyntheticEvent, useMemo, useState } from "react"
import { Autocomplete, Box, Button, Dialog, TextField } from "@mui/material"
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from "../../../../components/Dialog.styled"
import { Autoclave, SporetestingResult } from "../types"
import RepeatIcon from "@mui/icons-material/Repeat"
import { getAutoclaves, getSporetests, updateSporeTest } from "../../../../services/biologicalServices"
import { addError, addSuccess } from "../../../../redux/alerts"
import { useAppDispatch } from "../../../../redux/store"

type ReassignSporetestModal = {
  open: boolean,
  sporetest: SporetestingResult,
  autoclave: Autoclave,
  autoclaves: Autoclave[],
  setAutoclaves: Dispatch<SetStateAction<Autoclave[]>>,
  setSporetests: Dispatch<SetStateAction<SporetestingResult[]>>,
  handleClose: () => void,
  customerNumber: string,
}

const ReassignSporetestModal = (props: ReassignSporetestModal) => {
  const {
    open,
    sporetest,
    autoclave,
    autoclaves,
    setAutoclaves,
    setSporetests,
    handleClose,
    customerNumber,
  } = props

  const dispatch = useAppDispatch();
  const [isUpdating, setIsUpdating] = useState(false)
  const [selectedAutoclave, setSelectedAutoclave] = useState<Autoclave | null>(null)

  const disableSave = isUpdating || !selectedAutoclave

  const availableAutoclaves = useMemo(() => {
    const filtered = autoclaves.filter(ac => {
      return ac.autoclave_id !== autoclave.autoclave_id
    })

    const sorted = [...filtered].sort((a: Autoclave, b: Autoclave) => {
      return a.autoclave_name.localeCompare(b.autoclave_name)
    })

    return sorted
  }, [autoclaves])

  const handleSelectAutoclave = (event: SyntheticEvent<Element, Event>, value: Autoclave | null) => {
    setSelectedAutoclave(value)
  }

  const handleSave = async () => {
    if (selectedAutoclave) {
      try {
        setIsUpdating(true)

        const cloned = structuredClone(sporetest)

        cloned.autoclave_id = selectedAutoclave.autoclave_id

        await updateSporeTest(customerNumber, cloned)

        const results = await Promise.allSettled([
          getAutoclaves(customerNumber),
          getSporetests(customerNumber),
        ])

        const [autoclaveResult, sporetestResult] = results; 

        if (autoclaveResult.status === 'fulfilled') {
          setAutoclaves(autoclaveResult.value);
        } else {
          console.error(autoclaveResult.reason);
          dispatch(addError('Failed to load autoclaves'));
        }

        if (sporetestResult.status === 'fulfilled') {
          setSporetests(sporetestResult.value);
        } else {
          console.error(sporetestResult.reason);
          dispatch(addError('Failed to load sporetests'));
        }

        setIsUpdating(false)
        handleClose()

        dispatch(addSuccess('Spore test reassigned'))
        handleClose()
      } catch (err) {
        console.error(err)
        setIsUpdating(false)
        dispatch(addError(err instanceof Error ? err.message : 'There was a problem reassigning the spore test'))
        handleClose()        
      }
    }
  }  
  
  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <StyledDialogTitle>
        <Box className="flex flex-align-center c-g-8">
          <RepeatIcon fontSize="small" />
          <span>Reassign Spore Test</span>
        </Box>
      </StyledDialogTitle>
      <StyledDialogContent>
        <Box className="flex flex-center">
          <Autocomplete
            disabled={isUpdating}
            value={selectedAutoclave}
            onChange={handleSelectAutoclave}
            options={availableAutoclaves}
            getOptionLabel={option => String(option.autoclave_name)}
            isOptionEqualToValue={(option: Autoclave, value: Autoclave) => {
              return option.autoclave_id === value.autoclave_id
            }}
            size="small"
            style={{ width: '100%' }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select an Autoclave"
              />
            )
          }
          />
        </Box>
      </StyledDialogContent>
      <StyledDialogFooter>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={disableSave}>
          OK
        </Button>
      </StyledDialogFooter>
    </Dialog>
  )
}

export default ReassignSporetestModal
